import * as React from 'react';

/** A clock that ticks while something is live. Used by the Run Ledger so an
    in-flight run's elapsed time counts up between frames rather than freezing at
    whatever the last one said, and by the claim lease so an expert sees the
    countdown rather than discovering the expiry. Pass `active: false` and it
    stops entirely — an idle console must not re-render once a second forever. */
export function useNow(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs]);

  return now;
}

/** Debounced value, for the file filter over a large diff. */
export function useDebounced<T>(value: T, delayMs = 200): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

export function useDocumentTitle(title: string) {
  React.useEffect(() => {
    const previous = document.title;
    document.title = `${title} · hourz review`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/** Observed pixel height of an element. The diff virtualiser needs the real
    height of its scroll port, and a hard-coded one is wrong the moment the
    window resizes or the browser chrome changes. */
export function useElementHeight<T extends HTMLElement>(ref: React.RefObject<T | null>): number {
  const [height, setHeight] = React.useState(0);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setHeight(el.clientHeight);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return height;
}
